import React, { useEffect, useMemo, useRef } from 'react';
import type {
	DocNode,
	InlineExtensionDefinition,
	ParagraphDefinition,
	TextDefinition,
} from '@atlaskit/adf-schema';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import {
	useAiSpaceCreationSubmittedPrompt,
	useAiSpaceCreationSubmittedPromptActions,
	useAiSpaceCreationUploadedFiles,
	useAiSpaceCreationNudgePending,
	useAiSpaceCreationSingleInstrumentationId,
} from '@jira/ai-rovo_actions-store/src/AiSpaceCreationSubmittedPromptStore.tsx';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { HandleActionType, type ChatAction } from '@atlassian/rovo-conversation-actions/types';
import { fg } from '@atlassian/jira-feature-gating';
import { useConversationAssistantSidebarApi } from '@jira/platform__conversation-assistant/src/useConversationAssistantSidebarApi.tsx';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import { useAINudges } from '@atlassian/ai-smart-button-api/state';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { extractTextFromAdfDoc } from './extractTextFromAdfDoc.tsx';

const nudgeTitle = defineMessage({
	id: 'ai-rovo.ai-space-creation-nudge.title',
	defaultMessage: 'Suggest work items',
	description: 'Title for the AI space creation nudge shown on the floating Rovo button',
});

const nudgeSecondaryDescription = defineMessage({
	id: 'ai-rovo.ai-space-creation-nudge.secondary-description',
	defaultMessage: 'Tailored to your new space',
	description:
		'Secondary description revealed on hover for the AI space creation nudge, providing more detail about what Rovo can help with',
});

const inputBeforePrompt = defineMessage({
	id: 'ai-rovo.ai-space-creation-nudge.input-before-prompt',
	defaultMessage: 'based on the prompt used to create this space:',
	description: 'Text that appears before the user input in the prompt sent to Rovo',
});

const genericFallbackPrompt = defineMessage({
	id: 'ai-rovo.ai-space-creation-nudge.generic-fallback-prompt',
	defaultMessage: 'for my Jira Space',
	description:
		'Fallback prompt sent to Rovo when the AI space creation nudge is clicked but no user input is available',
});

const NUDGE_ID = 'ai-rovo.ai-space-creation-nudge';
const CREATE_WORK_ITEMS_SKILL_COMMAND = '/create-work-items';
const CREATE_WORK_ITEMS_SKILL_SLUG = CREATE_WORK_ITEMS_SKILL_COMMAND.slice(1);
const ROVO_SKILL_COMBINED_EXTENSION_KEY = 'skill:skillTag';
const ROVO_SKILL_EXTENSION_TYPE = 'com.atlassian.rovo.skill';

const createSkillNode = (slug: string): InlineExtensionDefinition => ({
	type: 'inlineExtension',
	attrs: {
		extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
		extensionType: ROVO_SKILL_EXTENSION_TYPE,
		parameters: { slug },
	},
});

/**
 * Builds an ADF document seeding the chat input with `introText` followed by the user's saved
 * prompt, preserving rich nodes.
 */
const buildNudgePromptAdf = (introText: string, body: DocNode | null): DocNode => {
	if (!body || !body.content?.length) {
		const fallbackParagraph: ParagraphDefinition = {
			type: 'paragraph',
			content: [{ type: 'text', text: introText }],
		};
		return { version: 1, type: 'doc', content: [fallbackParagraph] };
	}

	const introNode: TextDefinition = { type: 'text', text: `${introText} ` };
	const [firstBlock, ...restBlocks] = body.content;

	if (firstBlock.type === 'paragraph') {
		const mergedParagraph: ParagraphDefinition = {
			...firstBlock,
			content: [introNode, ...(firstBlock.content ?? [])],
		};
		return { version: 1, type: 'doc', content: [mergedParagraph, ...restBlocks] };
	}

	const introParagraph: ParagraphDefinition = {
		type: 'paragraph',
		content: [introNode],
	};
	return { version: 1, type: 'doc', content: [introParagraph, ...body.content] };
};

const prependCreateWorkItemsSkillCommandToAdf = (body: DocNode): DocNode => {
	const prefix = [
		createSkillNode(CREATE_WORK_ITEMS_SKILL_SLUG),
		{ type: 'text' as const, text: ' ' },
	];
	const [firstBlock, ...restBlocks] = body.content ?? [];

	if (firstBlock?.type === 'paragraph') {
		const paragraph: ParagraphDefinition = {
			...firstBlock,
			content: [...prefix, ...(firstBlock.content ?? [])],
		};
		return { ...body, version: 1, type: 'doc', content: [paragraph, ...restBlocks] };
	}

	return {
		...body,
		version: 1,
		type: 'doc',
		content: [{ type: 'paragraph', content: prefix }, ...(body.content ?? [])],
	};
};

export const AiRovoAiSpaceCreationNudgesProvider: React.FC<{}> = (): null => {
	const { formatMessage } = useIntl();
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const [submittedPrompt] = useAiSpaceCreationSubmittedPrompt();
	const [uploadedFiles] = useAiSpaceCreationUploadedFiles();
	const { clearSubmittedPrompt } = useAiSpaceCreationSubmittedPromptActions();
	const [nudgePending] = useAiSpaceCreationNudgePending();
	const [singleInstrumentationId] = useAiSpaceCreationSingleInstrumentationId();
	const sidebarApi = useConversationAssistantSidebarApi({});
	const { mode, toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const platformNudges = useAINudges();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const recoveryRef = useRef<{ nudge: ChatAction; confirmed: boolean } | null>(null);
	const hasStoredPrompt = submittedPrompt != null;
	const prompt = hasStoredPrompt
		? `${formatMessage(inputBeforePrompt)} ${extractTextFromAdfDoc(submittedPrompt)}`
		: formatMessage(genericFallbackPrompt);
	const promptAdf = useMemo(() => {
		if (!fg('rovo_insert_prompt_adf')) {
			return undefined;
		}
		const promptBodyAdf = hasStoredPrompt
			? buildNudgePromptAdf(formatMessage(inputBeforePrompt), submittedPrompt)
			: buildNudgePromptAdf(formatMessage(genericFallbackPrompt), null);
		return prependCreateWorkItemsSkillCommandToAdf(promptBodyAdf);
	}, [formatMessage, hasStoredPrompt, submittedPrompt]);

	useEffect(() => {
		if (!nudgePending) {
			return;
		}

		// Rovo chat is currently open - close it so the nudge can render
		if (mode !== undefined) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'chat closed', NUDGE_ID, {
				previousMode: mode,
			});
			toggleConversationAssistantUIMode({ sidebarApi });
		}

		const nudge: ChatAction = {
			id: singleInstrumentationId ? `${NUDGE_ID}:${singleInstrumentationId}` : NUDGE_ID,
			title: formatMessage(nudgeTitle),
			secondaryTitle: formatMessage(nudgeSecondaryDescription),
			icon: () => <AiGenerativeTextIcon label={formatMessage(nudgeTitle)} size="medium" />,
			handleType: HandleActionType.Chat,
			dynamicPrompt: `${CREATE_WORK_ITEMS_SKILL_COMMAND} ${prompt}`,
			dynamicPromptAdf: promptAdf,
			uploadedFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
			nudgeContext: {
				id: NUDGE_ID,
				selfEligible: true,
				...(singleInstrumentationId
					? { analyticsAttributes: { singleInstrumentationID: singleInstrumentationId } }
					: {}),
			},
			encodeDynamicPromptAsSkill: true,
		};

		recoveryRef.current = { nudge, confirmed: false };
		updateConfiguration({ nudges: [nudge] });
		clearSubmittedPrompt();
	}, [
		nudgePending,
		clearSubmittedPrompt,
		formatMessage,
		updateConfiguration,
		uploadedFiles,
		prompt,
		promptAdf,
		mode,
		sidebarApi,
		toggleConversationAssistantUIMode,
		createAnalyticsEvent,
		singleInstrumentationId,
	]);

	// Re-push once if the platform drops our nudge (e.g. chat-close / remount race).
	useEffect(() => {
		const state = recoveryRef.current;
		if (!state) {
			return;
		}
		const stillRegistered = platformNudges?.some((n) => n.id === state.nudge.id) ?? false;
		if (stillRegistered) {
			state.confirmed = true;
			return;
		}
		if (!state.confirmed) {
			return;
		}
		recoveryRef.current = null;
		updateConfiguration({ nudges: [state.nudge] });
	}, [platformNudges, updateConfiguration]);

	return null;
};
